import { DEFAULT_LANGUAGE, Interview } from '@cibli/shared';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { AuthProvider } from '@/features/auth/contexts/auth.context';
import { BillingProvider } from '@/features/common/contexts/billing.context';
import { CVTemplateProvider } from '@/features/common/contexts/cv-template.context';
import { LanguageProvider } from '@/features/common/contexts/language.context';
import { TrackingProvider } from '@/features/common/contexts/tracking.context';
import { UserProvider, useUserContext } from '@/features/common/contexts/user.context';
import { RegisterProvider } from '@/features/kiosk/contexts/register.context';
import { useFlowReset } from '@/features/kiosk/hooks/useFlowReset';
import { BoothProvider } from '@/features/settings/contexts/booth.context';
import { NetworkMonitorProvider } from '@/features/settings/contexts/network-monitor.context';
import { setApiLanguage } from '@/services/api';
import { createInterview } from '@/services/interview';
import { InterviewSource, parseInterviewSourceFromSearch } from '@/types/interview-source';

export interface InterviewContextState {
  interview: Interview | null;
  setInterview: (interview: Interview) => void;
  /** Creates interview (optionally with payment), sets it in context. Returns interview or null if no user. */
  createAndSetInterview: (paymentIntentId?: string, userId?: string) => Promise<Interview | null>;
  sleepMode: boolean;
  setSleepMode: (sleepMode: boolean) => void;
  /** Source of the interview flow (from query param). Used for redirect on cancel/logout. */
  interviewSource: InterviewSource;
  /** Whether the interview was started from dashboard. Used for redirect on cancel/logout. */
  cameFromDashboard: boolean;
  /** Whether the interview was started from dashboard. Used for redirect on cancel/logout. */
  comesFromDashboard: boolean;
  /** Quit session: reset tracking, logout on booth, clear OTP, go home. */
  onReset: (step?: number) => Promise<void>;
  /** Clear OTP state and navigate to onboarding. */
  handleBackToOnboardingWithReset: () => void;
}

const InterviewContext = createContext<InterviewContextState | undefined>(undefined);

interface InterviewProviderProps {
  children: ReactNode;
}

interface InterviewProviderInnerProps extends InterviewProviderProps {
  interview: Interview | null;
  setInterview: (interview: Interview | null) => void;
}

function InterviewProviderInner({
  children,
  interview,
  setInterview,
}: InterviewProviderInnerProps) {
  const location = useLocation();
  const { user } = useUserContext();
  const [interviewSource, setInterviewSource] = useState<InterviewSource>(InterviewSource.Booth);
  const [sleepMode, setSleepMode] = useState(false);
  /** Set to true when we ever see source=Dashboard (e.g. 5174→5173). Stays true for the session so HomeGuard knows "came from dashboard" on return to home. */
  const [cameFromDashboard, setCameFromDashboard] = useState(false);

  /** Current source from URL. On home there is no source so false; on /interview?source=Dashboard true. */
  const comesFromDashboard = useMemo(
    () => interviewSource === InterviewSource.Dashboard,
    [interviewSource],
  );

  const createAndSetInterview = useCallback(
    async (paymentIntentId?: string, userId?: string): Promise<Interview | null> => {
      const id = userId ?? user?.id;
      if (!id) return null;
      try {
        const newInterview = await createInterview(id, paymentIntentId);
        setInterview(newInterview);
        return newInterview;
      } catch (error) {
        console.error('Failed to create interview:', error);
        throw error;
      }
    },
    [user?.id, setInterview],
  );

  // Parse source from URL on any route (e.g. /?source=Dashboard when 5174 redirects to 5173 home)
  useEffect(() => {
    const source = parseInterviewSourceFromSearch(location.search);
    setInterviewSource(source);
    if (source === InterviewSource.Dashboard) {
      setCameFromDashboard(true);
    }
  }, [location.search]);

  const value: InterviewContextState = {
    interview,
    setInterview,
    createAndSetInterview,
    sleepMode,
    setSleepMode,
    interviewSource,
    cameFromDashboard,
    comesFromDashboard,
    onReset: async () => {},
    handleBackToOnboardingWithReset: () => {},
  };

  return <InterviewContext.Provider value={value}>{children}</InterviewContext.Provider>;
}

function InterviewContextResetInjector({ children }: { children: ReactNode }) {
  const context = useInterviewContext();
  const { onReset, handleBackToOnboardingWithReset } = useFlowReset();
  return (
    <InterviewContext.Provider value={{ ...context, onReset, handleBackToOnboardingWithReset }}>
      {children}
    </InterviewContext.Provider>
  );
}

/**
 * Provider for interview session state (current interview, sleepMode, interviewSource, onReset).
 * Wraps with UserProvider and Register/Tracking/Language/Billing so onReset is available.
 */
export const InterviewProvider: React.FC<InterviewProviderProps> = ({ children }) => {
  const [interview, setInterview] = useState<Interview | null>(null);

  const onLogout = useCallback(() => {
    setInterview(null);
    setApiLanguage(DEFAULT_LANGUAGE);
  }, []);

  return (
    <NetworkMonitorProvider>
      <BoothProvider>
        <AuthProvider>
          <CVTemplateProvider>
            <UserProvider onLogout={onLogout}>
              <InterviewProviderInner interview={interview} setInterview={setInterview}>
                <RegisterProvider>
                  <TrackingProvider>
                    <LanguageProvider>
                      <BillingProvider>
                        <InterviewContextResetInjector>{children}</InterviewContextResetInjector>
                      </BillingProvider>
                    </LanguageProvider>
                  </TrackingProvider>
                </RegisterProvider>
              </InterviewProviderInner>
            </UserProvider>
          </CVTemplateProvider>
        </AuthProvider>
      </BoothProvider>
    </NetworkMonitorProvider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useInterviewContext = (): InterviewContextState => {
  const context = useContext(InterviewContext);

  if (context === undefined) {
    throw new Error('useInterviewContext must be used within an InterviewProvider');
  }

  return context;
};

export default InterviewContext;
